import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { BsFiletypeXls } from "react-icons/bs";
import { FiArrowLeft, FiUsers } from "react-icons/fi";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { getActionTypeLabel } from "../../OtherData/actionTypeMap";
import { errorAlert } from "../../Components/Alerts";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import {
    accountDetailColumns,
    accountDetailMappings,
    accountMappings,
    accountsColumns,
} from "../../Components/ExcelConverter/AccountingReportsHeader";
import "./AccountsReportPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const LEDGER_ID = 302;

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

function formatDateForDisplay(date) {
    if (!date) {
        return "";
    }

    if (date instanceof Date) {
        if (Number.isNaN(date.getTime())) {
            return "";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    const raw = String(date).trim();
    if (!raw) {
        return "";
    }

    const dottedMatch = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dottedMatch) {
        return raw;
    }

    const slashMatch = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (slashMatch) {
        const [, year, month, day] = slashMatch;
        return `${day}.${month}.${year}`;
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${day}.${month}.${year}`;
    }

    const parsedDate = new Date(raw);
    if (Number.isNaN(parsedDate.getTime())) {
        return raw;
    }

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}.${month}.${year}`;
}

export default function AccountsReportPage() {
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [quickFilter, setQuickFilter] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [totals, setTotals] = useState({
        totalDebit: 0,
        totalCredit: 0,
        totalBalance: 0,
    });
    const [companiesData, setCompaniesData] = useState([]);
    const [logoPath, setLogoPath] = useState("");

    const [showDetails, setShowDetails] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [accountDetails, setAccountDetails] = useState([]);
    const [accountDetailName, setAccountDetailName] = useState("");
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsPaginationModel, setDetailsPaginationModel] = useState({ page: 0, pageSize: 100 });
    const [detailsSortModel, setDetailsSortModel] = useState([]);
    const [detailsFilterModel, setDetailsFilterModel] = useState({ items: [] });
    const [detailsQuickFilter, setDetailsQuickFilter] = useState([]);
    const [detailsRowCount, setDetailsRowCount] = useState(0);
    const [detailsTotals, setDetailsTotals] = useState({ debit: 0, credit: 0, progressive: 0 });
    const [detailsDateSortDirection, setDetailsDateSortDirection] = useState("desc");
    const detailsFetchTokenRef = useRef(0);

    const fetchCompaniesData = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
            const companyItems = Array.isArray(response?.data) ? response.data : [];
            setCompaniesData(companyItems);
            setLogoPath(companyItems[0]?.CompanyLogo ? ApiLink.PHOTO_URL + companyItems[0].CompanyLogo : "");
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave të kompanisë: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };

    const buildParams = (page, pageSize, sort, filter, quick) => ({
        ledger: LEDGER_ID,
        fiscalYear,
        page,
        pageSize,
        sort: JSON.stringify(sort || []),
        filter: JSON.stringify(filter || []),
        quickFilter: (quick || []).join(" "),
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + "AccountsReport/getAllAccountsReportData", {
                headers,
                params: buildParams(
                    paginationModel.page,
                    paginationModel.pageSize,
                    sortModel,
                    filterModel.items || [],
                    quickFilter,
                ),
            });

            const payload = response?.data;
            const items = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
            const total = Array.isArray(payload)
                ? items.length
                : Number(payload?.total ?? payload?.totalRows ?? items.length);
            const payloadTotals = payload?.totals || {};

            setData(items);
            setRowCount(total);
            setTotals({
                totalDebit: Number(payloadTotals.TotalDebit ?? payloadTotals.totalDebit ?? 0),
                totalCredit: Number(payloadTotals.TotalCredit ?? payloadTotals.totalCredit ?? 0),
                totalBalance: Number(payloadTotals.TotalBalance ?? payloadTotals.totalBalance ?? 0),
            });
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fiscalYear, paginationModel, sortModel, filterModel, quickFilter]);

    useEffect(() => {
        fetchCompaniesData();
    }, [fiscalYear]);

    const handleViewDetails = async (rowPayload) => {
        const accountId = rowPayload?.AccountID || rowPayload?.rowInfo?.AccountID;
        if (!accountId) {
            return;
        }

        setSelectedAccountId(accountId);
        setAccountDetailName(rowPayload?.AccountName || rowPayload?.rowInfo?.AccountName || "");
        setDetailsPaginationModel({ page: 0, pageSize: 100 });
        setDetailsSortModel([]);
        setDetailsFilterModel({ items: [] });
        setDetailsQuickFilter([]);
        setShowDetails(true);
    };

    const fetchDetailsData = async () => {
        if (!showDetails || !selectedAccountId) {
            return;
        }

        const requestToken = detailsFetchTokenRef.current + 1;
        detailsFetchTokenRef.current = requestToken;
        setDetailsLoading(true);
        try {
            const response = await axios.get(ApiLink.API_URL + "AccountsReport/detailsPaged", {
                headers,
                params: {
                    accountId: selectedAccountId,
                    fiscalYear,
                    page: detailsPaginationModel.page,
                    pageSize: detailsPaginationModel.pageSize,
                    sort: JSON.stringify(detailsSortModel),
                    filter: JSON.stringify(detailsFilterModel.items || []),
                    quickFilter: detailsQuickFilter.join(" "),
                },
            });

            const payload = response?.data;
            const items = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
            const total = Array.isArray(payload)
                ? items.length
                : Number(payload?.total ?? payload?.totalRows ?? items.length);
            const totalsPayload = payload?.totals || {};

            if (requestToken !== detailsFetchTokenRef.current) {
                return;
            }

            setAccountDetails(items);
            setDetailsRowCount(total);
            setDetailsTotals({
                debit: Number(totalsPayload.totalDebit ?? 0),
                credit: Number(totalsPayload.totalCredit ?? 0),
                progressive: Number(
                    totalsPayload.totalProgressive
                        ?? (Number(totalsPayload.totalDebit ?? 0) - Number(totalsPayload.totalCredit ?? 0)),
                ),
            });
        } catch (error) {
            if (requestToken !== detailsFetchTokenRef.current) {
                return;
            }
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e detajeve: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë.");
            }
        } finally {
            if (requestToken === detailsFetchTokenRef.current) {
                setDetailsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchDetailsData();
    }, [
        showDetails,
        selectedAccountId,
        fiscalYear,
        detailsPaginationModel,
        detailsSortModel,
        detailsFilterModel,
        detailsQuickFilter,
    ]);

    const fetchAllAccountsRows = async () => {
        const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
        const response = await axios.get(ApiLink.API_URL + "AccountsReport/getAllAccountsReportData", {
            headers,
            params: buildParams(0, totalRowsToExport, sortModel, filterModel.items || [], quickFilter),
        });

        const payload = response?.data;
        return Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
    };

    const mapAccountsExportData = (items) => (
        items.map((item, index) => ({
            Sequence_no: index + 1,
            AccountName: item.AccountName || "",
            Address: item.Address || "",
            IdentificationNumber: item.IdentificationNumber || "",
            Debit: Number(item.Debit || 0),
            Credit: Number(item.Credit || 0),
            Balance: Number(item.Balance || 0),
        }))
    );

    const mapAccountDetailsExportData = (items) => (
        items.map((item) => ({
            ActionType: getActionTypeLabel(item.ActionType || ""),
            AccountName: item.AccountName || "",
            IdentificationNumber: item.IdentificationNumber || "",
            Comment: item.Comment || "",
            Date: formatDateForDisplay(item.Date),
            Debit: Number(item.Debit || 0),
            Credit: Number(item.Credit || 0),
            Progressive: Number(item.Progressive || 0),
        }))
    );

    const exportDatasetToExcel = (items, columns, mappings, fileName) => {
        if (!items || items.length === 0) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return;
        }

        const filteredData = items.map((item) => (
            columns.reduce((accumulator, column) => {
                accumulator[mappings[column] || column] = item[column];
                return accumulator;
            }, {})
        ));

        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(excelFile, `${fileName}.xlsx`);
    };

    const reportPeriodLabel = useMemo(() => `${fiscalYear}`, [fiscalYear]);

    const sanitizeFileName = (value, fallback) => {
        const cleaned = String(value || "")
            .trim()
            .replace(/[\\/:*?"<>|]/g, "")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");

        return cleaned || fallback;
    };

    const addLogo = (doc) => {
        if (!logoPath) {
            return;
        }

        doc.addImage(logoPath, "JPEG", 12, 10, 24, 24);
    };

    const addReportHeader = (doc, title, subtitle, periodLabel, rightMetaLabel = "") => {
        const company = companiesData[0];
        const pageWidth = doc.internal.pageSize.getWidth();

        addLogo(doc);

        doc.setDrawColor(40, 73, 69);
        doc.setLineWidth(0.5);
        doc.line(12, 38, pageWidth - 12, 38);

        doc.setTextColor(31, 58, 54);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(title, 40, 18);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(95, 127, 115);
        doc.text(subtitle, 40, 24);

        doc.setFontSize(9);
        doc.setTextColor(31, 58, 54);
        doc.text(`Periudha: ${periodLabel}`, 12, 46);
        if (rightMetaLabel) {
            doc.setFont("helvetica", "bold");
            doc.text(rightMetaLabel, pageWidth - 12, 46, { align: "right" });
        }

        if (!company) {
            return 52;
        }

        const companyDetails = [
            company.CompanyName,
            `Numri Identifikues: ${company.IdentificationNumber || "-"}`,
            `Numri i TVSH-së: ${company.VATNumber || "-"}`,
            `Adresa: ${company.Address || "-"}`,
            `Tel: ${company.PhoneNumbers || "-"}`,
            `Email: ${company.Email || "-"}`,
        ];

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(31, 58, 54);
        doc.text(companyDetails[0], pageWidth - 12, 16, { align: "right" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(95, 127, 115);
        doc.text(companyDetails.slice(1), pageWidth - 12, 22, { align: "right" });

        return 52;
    };

    const addReportFooter = (doc, periodLabel, generatedOnLabel) => {
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const brandingLabel = "© www.accountree.net";
        const footerCenterLabel = `Gjeneruar më: ${generatedOnLabel} | ${brandingLabel}`;

        for (let page = 1; page <= pageCount; page += 1) {
            doc.setPage(page);
            doc.setDrawColor(212, 226, 219);
            doc.setLineWidth(0.3);
            doc.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(95, 127, 115);
            doc.text(`Periudha: ${periodLabel}`, 12, pageHeight - 5.5);
            doc.text(footerCenterLabel, pageWidth / 2, pageHeight - 5.5, { align: "center" });
            doc.text(`Faqe ${page} / ${pageCount}`, pageWidth - 12, pageHeight - 5.5, { align: "right" });
        }
    };

    const addPdfTable = (doc, headersRow, bodyRows, footRow, startY, baseColumnStyles) => {
        const horizontalMargin = 12;
        const availableTableWidth = doc.internal.pageSize.getWidth() - (horizontalMargin * 2);
        const baseTableWidth = Object.values(baseColumnStyles).reduce(
            (sum, style) => sum + Number(style.cellWidth || 0),
            0,
        );
        const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
        const columnStyles = Object.entries(baseColumnStyles).reduce((accumulator, [key, style]) => {
            accumulator[key] = {
                ...style,
                cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3)),
            };
            return accumulator;
        }, {});

        doc.autoTable({
            head: [headersRow],
            body: bodyRows,
            foot: [footRow],
            startY,
            showHead: "firstPage",
            showFoot: "lastPage",
            margin: {
                top: 16,
                left: horizontalMargin,
                right: horizontalMargin,
                bottom: 16,
            },
            theme: "grid",
            styles: {
                fontSize: 7.5,
                cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
                lineColor: [212, 226, 219],
                lineWidth: 0.1,
                textColor: [31, 58, 54],
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: [40, 73, 69],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
            },
            bodyStyles: {
                valign: "middle",
            },
            footStyles: {
                fillColor: [231, 240, 235],
                textColor: [31, 58, 54],
                fontStyle: "bold",
            },
            alternateRowStyles: {
                fillColor: [247, 250, 248],
            },
            didParseCell: (hookData) => {
                if (hookData.section !== "foot") {
                    return;
                }

                const alignRightFromIndex = headersRow.length >= 9 ? 6 : 4;
                if (hookData.column.index >= alignRightFromIndex) {
                    hookData.cell.styles.halign = "right";
                }
            },
            columnStyles,
        });
    };

    const buildAccountsPdfTableData = (items) => {
        let totalDebit = 0;
        let totalCredit = 0;
        let totalBalance = 0;

        const bodyRows = items.map((item, index) => {
            totalDebit += Number(item.Debit || 0);
            totalCredit += Number(item.Credit || 0);
            totalBalance += Number(item.Balance || 0);

            return [
                String(index + 1),
                item.AccountName || "",
                item.Address || "",
                item.IdentificationNumber || "",
                formatCurrency(item.Debit),
                formatCurrency(item.Credit),
                formatCurrency(item.Balance),
            ];
        });

        const footRow = [
            "",
            "",
            "Totali",
            "",
            formatCurrency(totalDebit),
            formatCurrency(totalCredit),
            formatCurrency(totalBalance),
        ];

        return { bodyRows, footRow };
    };

    const buildAccountDetailsPdfTableData = (items) => {
        let totalDebit = 0;
        let totalCredit = 0;

        const bodyRows = items.map((item, index) => {
            totalDebit += Number(item.Debit || 0);
            totalCredit += Number(item.Credit || 0);

            return [
                String(index + 1),
                getActionTypeLabel(item.ActionType || ""),
                item.AccountName || "",
                item.IdentificationNumber || "",
                item.Comment || "",
                formatDateForDisplay(item.Date),
                formatCurrency(item.Debit),
                formatCurrency(item.Credit),
                formatCurrency(item.Progressive),
            ];
        });

        const totalProgressive = totalDebit - totalCredit;

        const footRow = [
            "",
            "",
            "",
            "",
            "Totali",
            "",
            formatCurrency(totalDebit),
            formatCurrency(totalCredit),
            formatCurrency(totalProgressive),
        ];

        return { bodyRows, footRow };
    };

    const buildAccountsPdfDocument = async () => {
        const items = await fetchAllAccountsRows();
        if (!items.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const startY = addReportHeader(doc, "Llogaritë e Pagueshme", "Raport financiar i llogarive", reportPeriodLabel);
        const { bodyRows, footRow } = buildAccountsPdfTableData(items);

        addPdfTable(
            doc,
            ["#", "Emri Llogarisë", "Adresa", "Numri Identifikues", "Debi", "Kredi", "Bilanci"],
            bodyRows,
            footRow,
            startY,
            {
                0: { cellWidth: 8, halign: "center" },
                1: { cellWidth: 38 },
                2: { cellWidth: 34 },
                3: { cellWidth: 24 },
                4: { cellWidth: 20, halign: "right" },
                5: { cellWidth: 20, halign: "right" },
                6: { cellWidth: 20, halign: "right" },
            },
        );

        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            companyNameForFile: sanitizeFileName(companiesData?.[0]?.CompanyName, "Kompania"),
        };
    };

    const exportAccountsPdf = async () => {
        const generatedDocument = await buildAccountsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        const { doc, companyNameForFile } = generatedDocument;
        doc.save(`Llogarite_e_Pagueshme_${fiscalYear}_${companyNameForFile}.pdf`);
    };

    const fetchAllAccountDetailRows = async () => {
        if (!selectedAccountId) {
            return [];
        }

        const totalRowsToExport = Math.max(Number(detailsRowCount) || 0, 1);
        const response = await axios.get(ApiLink.API_URL + "AccountsReport/detailsPaged", {
            headers,
            params: {
                accountId: selectedAccountId,
                fiscalYear,
                page: 0,
                pageSize: totalRowsToExport,
                sort: JSON.stringify(detailsSortModel),
                filter: JSON.stringify(detailsFilterModel.items || []),
                quickFilter: detailsQuickFilter.join(" "),
            },
        });

        const payload = response?.data;
        return Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
    };

    const buildAccountDetailsPdfDocument = async () => {
        const detailItems = await fetchAllAccountDetailRows();
        if (!detailItems.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const accountHeaderLabel = accountDetailName ? `Llogaria: ${accountDetailName}` : "";
        const startY = addReportHeader(
            doc,
            "Llogaria e Furnitorit në Detale",
            "Raport i lëvizjeve të llogarisë",
            reportPeriodLabel,
            accountHeaderLabel,
        );
        const { bodyRows, footRow } = buildAccountDetailsPdfTableData(detailItems);

        addPdfTable(
            doc,
            ["#", "Tipi Veprimit", "Emri Llogarisë", "Numri Identifikues", "Komenti", "Data", "Debi", "Kredi", "Progresive"],
            bodyRows,
            footRow,
            startY,
            {
                0: { cellWidth: 8, halign: "center" },
                1: { cellWidth: 18 },
                2: { cellWidth: 34 },
                3: { cellWidth: 24 },
                4: { cellWidth: 34 },
                5: { cellWidth: 18, halign: "center" },
                6: { cellWidth: 18, halign: "right" },
                7: { cellWidth: 18, halign: "right" },
                8: { cellWidth: 18, halign: "right" },
            },
        );

        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            accountNameForFile: sanitizeFileName(accountDetailName, "Llogaria_Detaje"),
        };
    };

    const exportAccountDetailsPdf = async () => {
        const generatedDocument = await buildAccountDetailsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        const { doc, accountNameForFile } = generatedDocument;
        doc.save(`${accountNameForFile}.pdf`);
    };

    const printPdfDocument = (doc) => {
        const pdfBlob = doc.output("blob");
        const printUrl = URL.createObjectURL(pdfBlob);
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.left = "-10000px";
        printFrame.style.top = "0";
        printFrame.style.width = "1024px";
        printFrame.style.height = "768px";
        printFrame.style.border = "0";
        printFrame.style.opacity = "0";
        printFrame.style.pointerEvents = "none";
        let didPrint = false;
        let cleanupDone = false;

        const cleanup = () => {
            if (cleanupDone) {
                return;
            }

            cleanupDone = true;
            URL.revokeObjectURL(printUrl);
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        printFrame.onload = () => {
            if (didPrint) {
                return;
            }

            didPrint = true;

            try {
                const frameWindow = printFrame.contentWindow;
                if (!frameWindow) {
                    cleanup();
                    errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
                    return;
                }

                window.setTimeout(() => {
                    frameWindow.focus();
                    frameWindow.onafterprint = cleanup;
                    frameWindow.print();
                    window.setTimeout(cleanup, 5000);
                }, 450);
            } catch (error) {
                cleanup();
                errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
            }
        };

        document.body.appendChild(printFrame);
        printFrame.src = printUrl;

        window.setTimeout(() => {
            cleanup();
        }, 60000);
    };

    const handlePrintAccountsPdf = async () => {
        const generatedDocument = await buildAccountsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const handlePrintAccountDetailsPdf = async () => {
        const generatedDocument = await buildAccountDetailsPdfDocument();
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const handleExportMainXlsx = async () => {
        const items = await fetchAllAccountsRows();
        exportDatasetToExcel(
            mapAccountsExportData(items),
            accountsColumns,
            accountMappings,
            `Llogarite_e_Pagueshme_${fiscalYear}`,
        );
    };

    const handleExportDetailsXlsx = async () => {
        const detailItems = await fetchAllAccountDetailRows();
        exportDatasetToExcel(
            mapAccountDetailsExportData(detailItems),
            accountDetailColumns,
            accountDetailMappings,
            `${accountDetailName || "Llogaria_Detaje"}_Detaje`,
        );
    };

    const accountsColumnsGrid = useMemo(() => ([
        { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
        { field: "accName", headerName: "Emri Llogarisë", minWidth: 260, width: 300 },
        { field: "address", headerName: "Adresa", minWidth: 240, width: 280 },
        { field: "idNumber", headerName: "Numri Identifikues", minWidth: 160, width: 180 },
        { field: "debit", headerName: "Debi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "credit", headerName: "Kredi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "balance", headerName: "Bilanci", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    ]), []);

    const rows = useMemo(() => (
        data.map((item, index) => ({
            id: item.AccountID || `accounts-report-${index}`,
            rowNumber: paginationModel.page * paginationModel.pageSize + index + 1,
            accName: item.AccountName || "",
            address: item.Address || "",
            idNumber: item.IdentificationNumber || "",
            debit: item.Debit || 0,
            credit: item.Credit || 0,
            balance: item.Balance || 0,
            AccountID: item.AccountID,
            AccountName: item.AccountName,
            rowInfo: item,
        }))
    ), [data, paginationModel.page, paginationModel.pageSize]);

    const summaryFooter = useMemo(() => ({
        labelField: "idNumber",
        label: "Totali",
        values: {
            debit: formatCurrency(totals.totalDebit),
            credit: formatCurrency(totals.totalCredit),
            balance: formatCurrency(totals.totalBalance),
        },
    }), [totals]);

    const accountDetailsColumnsGrid = useMemo(() => ([
        { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
        { field: "type", headerName: "Tipi Veprimit", minWidth: 150, width: 170 },
        { field: "name", headerName: "Emri Llogarisë", minWidth: 220, width: 260 },
        { field: "idNumber", headerName: "Numri Identifikues", minWidth: 150, width: 180 },
        { field: "comment", headerName: "Komenti", minWidth: 220, width: 260 },
        { field: "date", headerName: "Data", minWidth: 120, width: 140 },
        { field: "debit", headerName: "Debi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "credit", headerName: "Kredi", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
        { field: "progressive", headerName: "Progresive", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    ]), []);

    const detailsRows = useMemo(() => (
        accountDetails.map((item, index) => ({
            id: item.JournalID || `account-detail-${index}`,
            rowNumber: index + 1,
            type: getActionTypeLabel(item.ActionType || ""),
            name: item.AccountName || "",
            idNumber: item.IdentificationNumber || "",
            comment: item.Comment || "",
            date: formatDateForDisplay(item.Date),
            debit: item.Debit || 0,
            credit: item.Credit || 0,
            progressive: item.Progressive || 0,
            rowInfo: item,
        }))
    ), [accountDetails]);

    const detailsSummaryFooter = useMemo(() => ({
        labelField: "date",
        label: "Totali",
        values: {
            debit: formatCurrency(detailsTotals.debit),
            credit: formatCurrency(detailsTotals.credit),
            progressive: formatCurrency(detailsTotals.progressive),
        },
    }), [detailsTotals]);

    const toggleDetailsDateSort = () => {
        setDetailsDateSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
        setDetailsSortModel((prev) => {
            const nextDirection = detailsDateSortDirection === "desc" ? "asc" : "desc";
            return [
                { field: "date", sort: nextDirection },
                { field: "journalId", sort: nextDirection },
            ];
        });
        setDetailsPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    return (
        <div className="Crud__Container ARP__page">
            <div className="ARP__content">
                {!showDetails && (
                    <>
                        <CrudPageHeader
                            icon={FiUsers}
                            title="Llogaritë e Pagueshme"
                            description="Forma e llogarive të pagueshme është e përshtatur për kontroll, filtrim dhe analizë profesionale të saldove sipas llogarive."
                            className="ARP__header"
                        />

                        <div className="ARP__reportGrid">
                            <ReusableAccountBooksGrid
                                columns={accountsColumnsGrid}
                                rows={rows}
                                loading={loading}
                                rowCount={rowCount}
                                height={650}
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                sortModel={sortModel}
                                setSortModel={setSortModel}
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                quickFilter={quickFilter}
                                setQuickFilter={setQuickFilter}
                                showViewAction={true}
                                onView={handleViewDetails}
                                showEditAction={false}
                                showDeleteAction={false}
                                showExportButton={false}
                                summaryFooter={summaryFooter}
                                toolbarActions={(
                                    <>
                                        <AddActionsButton
                                            text="Print"
                                            onClick={handlePrintAccountsPdf}
                                            icon={<AiOutlinePrinter />}
                                            className="RSDG__toolButton ARP__gridActionBtn--print"
                                        />
                                        <AddActionsButton
                                            text="PDF"
                                            onClick={exportAccountsPdf}
                                            icon={<AiOutlineFilePdf />}
                                            className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                        />
                                        <AddActionsButton
                                            text="XLSX"
                                            onClick={handleExportMainXlsx}
                                            icon={<BsFiletypeXls />}
                                            className="RSDG__toolButton ARP__gridActionBtn--xlsx"
                                        />
                                    </>
                                )}
                            />
                        </div>
                    </>
                )}

                {showDetails && (
                    <>
                        <CrudPageHeader
                            icon={FiUsers}
                            title={`Llogaria e Furnitorit në Detale${accountDetailName ? ` - ${accountDetailName}` : ""}`}
                            description="Shfaqen të gjitha lëvizjet për llogarinë e zgjedhur."
                            className="ARP__header"
                            action={(
                                <AddActionsButton
                                    text="Kthehu te lista"
                                    onClick={() => {
                                        detailsFetchTokenRef.current += 1;
                                        setShowDetails(false);
                                        setSelectedAccountId(null);
                                        setAccountDetails([]);
                                        setDetailsRowCount(0);
                                        setDetailsTotals({ debit: 0, credit: 0, progressive: 0 });
                                        setDetailsPaginationModel({ page: 0, pageSize: 100 });
                                        setDetailsSortModel([]);
                                        setDetailsFilterModel({ items: [] });
                                        setAccountDetailName("");
                                        setDetailsQuickFilter([]);
                                    }}
                                    icon={<FiArrowLeft />}
                                    className="ARP__backBtn"
                                />
                            )}
                        />

                        <div className="ARP__reportGrid ARP__reportGrid--details">
                            <ReusableAccountBooksGrid
                                columns={accountDetailsColumnsGrid}
                                rows={detailsRows}
                                loading={detailsLoading}
                                rowCount={detailsRowCount}
                                height={650}
                                paginationModel={detailsPaginationModel}
                                setPaginationModel={setDetailsPaginationModel}
                                sortModel={detailsSortModel}
                                setSortModel={setDetailsSortModel}
                                filterModel={detailsFilterModel}
                                setFilterModel={setDetailsFilterModel}
                                quickFilter={detailsQuickFilter}
                                setQuickFilter={setDetailsQuickFilter}
                                showViewAction={false}
                                showEditAction={false}
                                showDeleteAction={false}
                                showExportButton={false}
                                summaryFooter={detailsSummaryFooter}
                                toolbarActions={(
                                    <>
                                        <AddActionsButton
                                            text="Print"
                                            onClick={handlePrintAccountDetailsPdf}
                                            icon={<AiOutlinePrinter />}
                                            className="RSDG__toolButton ARP__gridActionBtn--print"
                                        />
                                        <AddActionsButton
                                            text="PDF"
                                            onClick={exportAccountDetailsPdf}
                                            icon={<AiOutlineFilePdf />}
                                            className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                        />
                                        <AddActionsButton
                                            text="XLSX"
                                            onClick={handleExportDetailsXlsx}
                                            icon={<BsFiletypeXls />}
                                            className="RSDG__toolButton ARP__gridActionBtn--xlsx"
                                        />
                                        <button
                                            type="button"
                                            className="ARP__detailsSortToggle"
                                            onClick={toggleDetailsDateSort}
                                            title="Ndërro renditjen e datës"
                                        >
                                            Sortimi: Data {detailsDateSortDirection === "desc" ? "↓ DESC" : "↑ ASC"}
                                        </button>
                                    </>
                                )}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}